"use client";

import { errorLoggingService } from "@/lib/error-logging-service";
import { auth, db, storage } from "@/lib/firebase-client";
import { syncUserProfileToRecipeBoxes } from "@/lib/recipe-box-service";
import type { AccountTier, UserProfileDocument, ViewPreference } from "@/lib/types";
import { updateProfile, type User } from "firebase/auth";
import {
    doc,
    runTransaction,
    serverTimestamp,
    updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

const USERS_COLLECTION = "users";
const DEFAULT_ACCOUNT_TIER: AccountTier = "free";

type ProfileInput = Partial<
  Omit<UserProfileDocument, "id" | "createdAt" | "updatedAt">
>;

type ProfileUpdateOptions = {
  displayName?: string;
  avatarFile?: File | null;
};

const sanitizePayload = (payload: ProfileInput): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null),
  );

export async function ensureUserProfileDocument(
  user: User,
  overrides: ProfileInput = {},
): Promise<void> {
  const docRef = doc(db, USERS_COLLECTION, user.uid);
  const sanitizedOverrides = sanitizePayload(overrides);

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(docRef);

    if (!snapshot.exists()) {
      const data: Omit<UserProfileDocument, "id"> = {
        email: (sanitizedOverrides.email as string | undefined) ?? user.email ?? "",
        displayName:
          (sanitizedOverrides.displayName as string | undefined) ??
          user.displayName ??
          "",
        avatarURL:
          (sanitizedOverrides.avatarURL as string | undefined) ??
          user.photoURL ??
          "",
        joinedRecipeBoxes:
          (sanitizedOverrides.joinedRecipeBoxes as string[] | undefined) ?? [],
        accountTier:
          (sanitizedOverrides.accountTier as AccountTier | undefined) ?? DEFAULT_ACCOUNT_TIER,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      transaction.set(docRef, data);
      return;
    }

    if (Object.keys(sanitizedOverrides).length === 0) {
      return;
    }

    const updatePayload: Record<string, unknown> = {
      ...sanitizedOverrides,
      updatedAt: serverTimestamp(),
    };

    transaction.update(docRef, updatePayload);
  });
}

export async function updateUserProfileDocument(
  user: User,
  { displayName, avatarFile }: ProfileUpdateOptions,
): Promise<{ avatarURL?: string }> {
  await ensureUserProfileDocument(user);

  const docRef = doc(db, USERS_COLLECTION, user.uid);
  const updates: Record<string, unknown> = {};
  const authUpdates: Record<string, string> = {};

  const trimmedDisplayName = displayName?.trim();
  if (trimmedDisplayName) {
    updates.displayName = trimmedDisplayName;
    authUpdates.displayName = trimmedDisplayName;
  }

  let avatarURL: string | undefined;
  if (avatarFile) {
    avatarURL = await uploadProfileAvatar(user.uid, avatarFile);
    updates.avatarURL = avatarURL;
    authUpdates.photoURL = avatarURL;
  }

  if (Object.keys(updates).length > 0) {
    updates.updatedAt = serverTimestamp();
    await updateDoc(docRef, updates);
  }

  if (Object.keys(authUpdates).length > 0) {
    await updateProfile(user, authUpdates);
    await user.reload();
  }

  // Sync profile changes to all recipe boxes where user is a member
  // This ensures member avatars and display names stay up to date
  const profileSyncUpdates: {
    displayName?: string;
    photoURL?: string;
    email?: string;
  } = {};

  if (trimmedDisplayName) {
    profileSyncUpdates.displayName = trimmedDisplayName;
  }
  if (avatarURL) {
    profileSyncUpdates.photoURL = avatarURL;
  }
  if (user.email) {
    profileSyncUpdates.email = user.email;
  }

  if (Object.keys(profileSyncUpdates).length > 0) {
    // Don't await - let this run in the background to avoid blocking the UI
    syncUserProfileToRecipeBoxes(user.uid, profileSyncUpdates).catch((error) => {
      errorLoggingService.logError({
        context: 'updateUserProfile - sync to recipe boxes',
        error: error as Error,
        additionalData: { userId: user.uid },
      });
      // Non-critical error - profile update succeeded, just member data sync failed
    });
  }

  return { avatarURL };
}

async function uploadProfileAvatar(userId: string, file: File): Promise<string> {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
  const safeName = file.name.replaceAll(/[^a-zA-Z0-9.\-_]/g, "");
  const defaultName = `avatar.${extension}`;
  const fileName = `${Date.now()}-${safeName || defaultName}`;
  const storageRef = ref(storage, `avatars/${userId}/${fileName}`);

  await uploadBytes(storageRef, file, {
    contentType: file.type || "image/jpeg",
  });

  return getDownloadURL(storageRef);
}

export async function updateViewPreference(
  user: User,
  viewPreference: ViewPreference,
): Promise<void> {
  await ensureUserProfileDocument(user);
  const docRef = doc(db, USERS_COLLECTION, user.uid);
  await updateDoc(docRef, {
    viewPreference,
    updatedAt: serverTimestamp(),
  });
}

// Web push notifications removed (no service workers / no PWA).

export const signOutCurrentUser = () => auth.signOut();
